/**
 * Pagina «Scopri» (SPEC-015 §8): form del nuovo tema + elenco delle ricerche
 * passate.
 *
 * Il tema è testo libero e resta tale: nessun suggerimento automatico che lo
 * riscriva: è la motivazione dell'utente a orientare la ricerca, ed è ciò che
 * la valutazione finale deve poter citare.
 */

import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Alert, Badge, Button, Card, DomainBadge, EmptyState, Field, Select, Spinner, Switch, Textarea } from '@/ui';
import { ASSET_DOMAIN_LABELS, type AssetDomain } from '@/lib/asset-domain';
import { formatDateTime } from '@/lib/format';
import { STATUS_LABEL, STATUS_TONE } from './discovery-labels';
import { useCreateDiscoveryRun, useDiscoveryQuota, useDiscoveryRuns, type CreateRunInput } from './api';
import './discovery.css';

/** I mercati proposti nella tendina: quelli su cui l'utente opera davvero. */
const DOMAIN_OPTIONS: Array<{ value: AssetDomain; label: string }> = [
  { value: 'stock', label: ASSET_DOMAIN_LABELS.stock },
  { value: 'crypto', label: ASSET_DOMAIN_LABELS.crypto },
];

const MARKET_OPTIONS = [
  { value: 'global', label: 'Tutti i mercati' },
  { value: 'XMIL', label: 'Borsa Italiana (Milano)' },
  { value: 'XETR', label: 'Xetra (Francoforte)' },
  { value: 'XPAR', label: 'Euronext Paris' },
  { value: 'XAMS', label: 'Euronext Amsterdam' },
  { value: 'XLON', label: 'London Stock Exchange' },
  { value: 'XNAS', label: 'Nasdaq' },
  { value: 'XNYS', label: 'New York Stock Exchange' },
  { value: 'XSWX', label: 'SIX Swiss Exchange' },
];

export function DiscoveryPage() {
  const navigate = useNavigate();
  const runs = useDiscoveryRuns();
  const quota = useDiscoveryQuota();
  const createRun = useCreateDiscoveryRun();

  // SPEC-026 §5.1: l'ambito è il PRIMO parametro, perché cambia cosa si cerca
  // (monete, non società), come si identifica e quali filtri hanno senso.
  const [domain, setDomain] = useState<AssetDomain>('stock');
  const [themeTitle, setThemeTitle] = useState('');
  const [themeDescription, setThemeDescription] = useState('');
  const [market, setMarket] = useState('global');
  const [includeEtfs, setIncludeEtfs] = useState(true);
  const [excludeOwned, setExcludeOwned] = useState(true);
  const [trendingOnly, setTrendingOnly] = useState(false);
  const [onlyNew, setOnlyNew] = useState(false);
  const [maxResults, setMaxResults] = useState('10');

  const exhausted = quota.data ? quota.data.remaining <= 0 : false;

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    const input: CreateRunInput = {
      themeTitle: themeTitle.trim(),
      ...(themeDescription.trim() ? { themeDescription: themeDescription.trim() } : {}),
      filters: {
        domain,
        // In crypto un exchange non ha paese e «ETF» non è una moneta: il
        // backend li forza comunque, qui non si mandano valori senza senso.
        marketScope: domain === 'crypto' || market === 'global' ? { kind: 'global' } : { kind: 'markets', mics: [market] },
        includeEtfs: domain === 'crypto' ? false : includeEtfs,
        ...(domain === 'crypto' ? { assetTypes: ['crypto' as const] } : {}),
        excludeOwned,
        trendingOnly,
        ...(onlyNew ? { noveltyMonths: 24 } : {}),
        maxResults: Number(maxResults),
      },
    };
    createRun.mutate(input, { onSuccess: (run) => navigate(`/discovery/${run.id}`) });
  };

  return (
    <div className="page">
      <header className="page__head">
        <div>
          <h1>Scopri</h1>
          <p className="page__subtitle">
            Descrivi un tema di investimento a parole tue: la ricerca cerca sul web i titoli quotati
            che vi sono esposti e ti spiega perché.
          </p>
        </div>
        {quota.data && (
          <Badge tone={exhausted ? 'negative' : 'neutral'}>
            {quota.data.remaining} di {quota.data.limit} ricerche oggi
          </Badge>
        )}
      </header>

      {/* Sottomenu di Scopri: la scoperta tematica risponde a «chi è esposto a
          questo tema», il titolo del giorno a «domani succede qualcosa a
          qualcuno». Due domande diverse, stessa sezione. */}
      <nav className="discovery-subnav" aria-label="Sezioni di Scopri">
        <span className="discovery-subnav__current" aria-current="page">Scoperta tematica</span>
        <Link to="/discovery/daily-pick">Titolo del giorno →</Link>
      </nav>

      <Card title="Nuova ricerca">
        <form onSubmit={handleSubmit} className="discovery-form">
          <div className="discovery-form__domain" role="radiogroup" aria-label="Ambito della ricerca">
            {DOMAIN_OPTIONS.map((option) => (
              <label key={option.value} className={domain === option.value ? 'active' : ''}>
                <input
                  type="radio"
                  name="domain"
                  value={option.value}
                  checked={domain === option.value}
                  onChange={() => setDomain(option.value)}
                />
                {option.label}
              </label>
            ))}
            <span className="cell--minor">
              {domain === 'crypto'
                ? 'Criptovalute e token nativi, entro le prime 200 per capitalizzazione. Mai ETF o società quotate.'
                : 'Azioni ed ETF quotati in borsa.'}
            </span>
          </div>
          <Field
            label="Tema"
            value={themeTitle}
            onChange={(event) => setThemeTitle(event.target.value)}
            placeholder="Climatizzazione / HVAC"
            maxLength={80}
            required
            hint="Da 3 a 80 caratteri"
          />
          <Textarea
            label="Perché ti interessa"
            value={themeDescription}
            onChange={(event) => setThemeDescription(event.target.value)}
            placeholder="Penso sarà importante sia per il clima caldo sia per il raffreddamento dei data center."
            maxLength={600}
            rows={3}
            hint="Facoltativo ma consigliato: orienta la ricerca e viene citato nelle motivazioni."
          />

          <div className="discovery-form__row">
            {/* In crypto il mercato non esiste come scelta: nascosto, non disabilitato. */}
            {domain === 'stock' && (
              <Select
                label="Mercato"
                value={market}
                onChange={(event) => setMarket(event.target.value)}
                options={MARKET_OPTIONS}
              />
            )}
            <Select
              label="Quanti titoli"
              value={maxResults}
              onChange={(event) => setMaxResults(event.target.value)}
              options={[
                { value: '5', label: '5' },
                { value: '10', label: '10' },
                { value: '15', label: '15' },
                { value: '20', label: '20' },
              ]}
            />
          </div>

          <div className="discovery-form__switches">
            {domain === 'stock' && <Switch label="Includi ETF tematici" checked={includeEtfs} onChange={setIncludeEtfs} />}
            <Switch label="Escludi quelli che ho già" checked={excludeOwned} onChange={setExcludeOwned} />
            <Switch label="Solo con notizie recenti" checked={trendingOnly} onChange={setTrendingOnly} />
            <Switch label="Cerca anche quotazioni recenti" checked={onlyNew} onChange={setOnlyNew} />
          </div>

          {exhausted && (
            <Alert variant="warning" title="Ricerche di oggi esaurite">
              Il limite si azzera {quota.data ? formatDateTime(quota.data.resetAt) : 'domani'}.
            </Alert>
          )}
          {createRun.isError && (
            <Alert variant="error" title="Ricerca non avviata">
              {createRun.error instanceof Error ? createRun.error.message : 'Errore imprevisto.'}
            </Alert>
          )}

          <Button type="submit" disabled={createRun.isPending || exhausted || themeTitle.trim().length < 3}>
            {createRun.isPending ? 'Avvio…' : domain === 'crypto' ? 'Cerca criptovalute' : 'Cerca titoli'}
          </Button>
          <p className="cell--minor">La ricerca richiede circa 1-4 minuti e continua anche se chiudi la pagina.</p>
        </form>
      </Card>

      <Card title="Ricerche precedenti">
        {runs.isLoading && <Spinner />}
        {runs.data && runs.data.items.length === 0 && (
          <EmptyState
            icon="🔎"
            title="Nessuna ricerca ancora"
            description="Descrivi un tema qui sopra per cominciare."
          />
        )}
        {runs.data && runs.data.items.length > 0 && (
          <ul className="discovery-runs">
            {runs.data.items.map((run) => (
              <li key={run.id} className="discovery-runs__item">
                <Link to={`/discovery/${run.id}`} className="discovery-runs__link">
                  <span className="discovery-runs__title">{run.themeTitle}</span>
                  <span className="cell--minor">{formatDateTime(run.createdAt)}</span>
                </Link>
                <div className="discovery-runs__meta">
                  {run.domain === 'crypto' && <DomainBadge domain="crypto" />}
                  <Badge tone={STATUS_TONE[run.status]}>{STATUS_LABEL[run.status]}</Badge>
                  {run.status === 'completed' && (
                    <span className="cell--minor">{run.resultCount ?? 0} titoli</span>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}
